/**
 * Copy helper avec fallback non-secure context.
 *
 * Pourquoi
 * --------
 * `navigator.clipboard.writeText()` est restreint aux **secure contexts**
 * (HTTPS ou `http://localhost`). L'app frontend peut tourner sur des hôtes
 * non-sécurisés (LAN, Tailscale, prévisualisations dev) → l'API clipboard
 * moderne est absente et toutes les copies plantent silencieusement.
 *
 * Le fallback `document.execCommand('copy')` n'est pas restreint et marche
 * en HTTP. Officiellement déprécié mais TOUJOURS supporté par tous les
 * navigateurs courants en 2026.
 *
 * Si les 2 voies échouent, on rejette avec une erreur pour que le caller
 * puisse afficher sa propre toast.
 */
export async function copyToClipboard(text: string): Promise<void> {
    if (!text) {
        throw new Error("Nothing to copy");
    }

    // 1. Voie moderne : Clipboard API (HTTPS / localhost only).
    if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function" &&
        window.isSecureContext
    ) {
        try {
            await navigator.clipboard.writeText(text);
            return;
        } catch (e) {
            // Permission refusée ou autre — on tente quand même le fallback ci-dessous.
        }
    }

    // 2. Fallback historique : <textarea> + execCommand('copy').
    // Marche en HTTP non-sécurisé.
    const textarea = document.createElement("textarea");
    textarea.value = text;
    // Hors écran mais focusable — certaines variantes Safari refusent
    // execCommand si l'élément n'est pas dans le DOM ou hors viewport.
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.width = "1px";
    textarea.style.height = "1px";
    textarea.style.padding = "0";
    textarea.style.border = "none";
    textarea.style.outline = "none";
    textarea.style.boxShadow = "none";
    textarea.style.background = "transparent";
    textarea.style.opacity = "0";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);

    try {
        textarea.select();
        textarea.setSelectionRange(0, text.length); // iOS Safari needs this
        const ok = document.execCommand("copy");
        if (!ok) {
            throw new Error("document.execCommand('copy') returned false");
        }
    } finally {
        document.body.removeChild(textarea);
    }
}
